import type {
  BuildingGraphFaceSideDirection,
  BuildingGraphFaceSideHandle,
} from "building-types/buildingGraph/buildingGraphSideHandles";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { ResolvedWallPlaneSide, WallPlaneOperator } from "building-types/walls2/wallPlane";
import type { DocFaceDirection, DocWallPlaneFilter } from "building-types/walls2/wallPlaneFilters";
import { resolveBuildingGraphFaceSide, canonicalDirectionToResolvedSide } from "../buildingGraph/resolveFaceSide";
import { executeWallFaceCatalogFilter, type PrecomputedTopLevelMatches } from "./wallPlaneFilterExecution";

/**
 * Runtime-resolved wall plane operation: a side-aware handle paired with the
 * operator to apply.
 */
export interface ExecuteResolvedWallPlaneOperationResult {
  readonly handle: BuildingGraphFaceSideHandle;
  readonly operator: WallPlaneOperator;
  readonly resolvedFaceSide?: ResolvedWallPlaneSide;
}

export interface ResolvedWallPlaneSideHandleResult {
  readonly handle: BuildingGraphFaceSideHandle;
  readonly resolvedCanonicalDirectionInPlane?: BuildingGraphFaceSideDirection;
}

export interface RuntimeWallPlaneFilterOperation {
  readonly filter: DocWallPlaneFilter;
  readonly direction: DocFaceDirection;
  readonly operator: WallPlaneOperator;
}

export function* executeWallPlaneFilterOperationFromHandles(
  sideHandleResults: readonly ResolvedWallPlaneSideHandleResult[],
  operator: WallPlaneOperator,
): Iterable<ExecuteResolvedWallPlaneOperationResult> {
  for (const sideHandleResult of sideHandleResults) {
    let resolvedFaceSide = canonicalDirectionToResolvedSide(sideHandleResult.resolvedCanonicalDirectionInPlane);
    if (operator.operationType === "SET_WALL_FINISH" && operator.assemblySide === "NEGATIVE_NORMAL") {
      resolvedFaceSide = resolvedFaceSide === "RIGHT" ? "LEFT" : "RIGHT";
    }

    yield {
      handle: sideHandleResult.handle,
      operator,
      resolvedFaceSide,
    };
  }
}

export function* executeWallPlaneFilterOperation(
  operation: RuntimeWallPlaneFilterOperation,
  building: BuildingGraphFilterBuilding,
  precomputedTopLevelMatches?: PrecomputedTopLevelMatches,
  precomputedSideHandleResults?: readonly ResolvedWallPlaneSideHandleResult[],
): Iterable<ExecuteResolvedWallPlaneOperationResult> {
  const { filter, direction, operator } = operation;
  if (precomputedSideHandleResults) {
    yield* executeWallPlaneFilterOperationFromHandles(precomputedSideHandleResults, operator);
    return;
  }
  for (const handle of executeWallFaceCatalogFilter(filter, building, direction, precomputedTopLevelMatches)) {
    const resolved = resolveBuildingGraphFaceSide(handle, building);
    let resolvedFaceSide = canonicalDirectionToResolvedSide(resolved?.resolvedCanonicalDirectionInPlane);
    if (operator.operationType === "SET_WALL_FINISH" && operator.assemblySide === "NEGATIVE_NORMAL") {
      resolvedFaceSide = resolvedFaceSide === "RIGHT" ? "LEFT" : "RIGHT";
    }

    yield {
      handle,
      operator,
      resolvedFaceSide,
    };
  }
}
